import { useCallback, useEffect, useRef, useState } from "react";
import { RecaptchaVerifier, linkWithPhoneNumber, sendEmailVerification } from "firebase/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { fbAuth, fbError } from "../lib/firebase";
import api, { apiError } from "../lib/api";
import { MailCheck, Smartphone, ShieldCheck } from "lucide-react";
import { track } from "../lib/analytics";
import { toast } from "sonner";

const OTP_LOG_KEY = "regnav_otp_log";
const HOUR_MS = 3600000;
const COOLDOWN_MS = 60000;

function otpStatus() {
  let log = [];
  try {
    log = JSON.parse(localStorage.getItem(OTP_LOG_KEY)) || [];
  } catch {
    log = [];
  }
  const now = Date.now();
  log = log.filter((t) => now - t < HOUR_MS);
  const last = log[log.length - 1] || 0;
  return { log, hourCount: log.length, cooldownLeft: last ? Math.max(0, COOLDOWN_MS - (now - last)) : 0 };
}

const normalizePhone = (raw) => {
  const s = raw.replace(/[^\d+]/g, "");
  if (s.startsWith("+")) return s;
  if (s.length === 10) return `+91${s}`;
  return `+${s}`;
};

export function VerifyGate({ open, onOpenChange, onVerified }) {
  const { user, setUser, fbUser, exchangeFirebase } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [captchaMode, setCaptchaMode] = useState("invisible");
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const verifierRef = useRef(null);

  // (B) Clears the verifier and remounts the container div so a fresh instance can
  // always be created — reopening never throws "reCAPTCHA has already been rendered"
  const destroyCaptcha = useCallback(() => {
    try {
      verifierRef.current?.clear();
    } catch (e) {
      // widget may already be gone
      console.debug("verify-gate: captcha clear skipped", e);
    }
    verifierRef.current = null;
    setCaptchaNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!open) {
      destroyCaptcha();
      return;
    }
    setErr("");
    setCode("");
    setConfirmation(null);
    setPhone(user?.phone || "");
    setStep(user && !user.email_verified ? "email" : "phone");
    setCaptchaMode("invisible");
    destroyCaptcha();
    setCooldown(Math.ceil(otpStatus().cooldownLeft / 1000));
  }, [open, user, destroyCaptcha]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (emailCooldown <= 0) return;
    const t = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCooldown]);

  // In visible fallback mode, render the reCAPTCHA v2 checkbox immediately
  useEffect(() => {
    if (!open || step !== "phone" || captchaMode !== "visible" || verifierRef.current) return;
    const el = document.getElementById(`otp-recaptcha-${captchaNonce}`);
    if (!el) return;
    try {
      const v = new RecaptchaVerifier(fbAuth, el, { size: "normal" });
      verifierRef.current = v;
      v.render().catch((e) => console.error("reCAPTCHA render failed:", e?.code || e));
    } catch (e) {
      console.error("reCAPTCHA init failed:", e?.code || e);
    }
  }, [open, step, captchaMode, captchaNonce]);

  const syncPhoneToBackend = async () => {
    const idToken = await fbAuth.currentUser.getIdToken(true);
    const { data } = await api.post("/auth/verify-phone", { id_token: idToken });
    setUser(data);
    track("mobile_otp_verified");
    toast.success("Mobile number verified — you're all set");
    onVerified();
  };

  const resendEmail = async () => {
    if (!fbAuth.currentUser) return;
    try {
      await sendEmailVerification(fbAuth.currentUser);
      setEmailCooldown(60);
      toast.success("Verification email re-sent — check your inbox (and spam)");
    } catch (e) {
      console.error("Email verification send failed:", e?.code || e);
      setErr(fbError(e));
    }
  };

  const checkEmailVerified = async () => {
    if (!fbAuth.currentUser) return;
    setBusy(true);
    setErr("");
    try {
      await fbAuth.currentUser.reload();
      if (!fbAuth.currentUser.emailVerified) {
        setErr("Not verified yet — click the link in the email we sent you, then try again");
        return;
      }
      const data = await exchangeFirebase(fbAuth.currentUser, null, true);
      track("email_verified", { source: "gate_modal" });
      if (data.phone_verified) {
        toast.success("Email verified — you're all set");
        onVerified();
      } else {
        setStep("phone");
      }
    } catch (e) {
      console.error("Email verification check failed:", e?.code || e);
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendOtp = async () => {
    setErr("");
    const status = otpStatus();
    if (status.hourCount >= 3) {
      const mins = Math.ceil((HOUR_MS - (Date.now() - status.log[0])) / 60000);
      setErr(`OTP limit reached (3 per hour) — try again in about ${mins} min`);
      return;
    }
    if (status.cooldownLeft > 0) return;
    const normalized = normalizePhone(phone);
    if (normalized.length < 12) {
      setErr("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setBusy(true);
    try {
      if (!verifierRef.current) {
        const el = document.getElementById(`otp-recaptcha-${captchaNonce}`);
        verifierRef.current = new RecaptchaVerifier(fbAuth, el, {
          size: captchaMode === "visible" ? "normal" : "invisible",
        });
      }
      const conf = await linkWithPhoneNumber(fbAuth.currentUser, normalized, verifierRef.current);
      // (A)+(C) Advance, record the attempt and show "OTP sent" ONLY after Firebase
      // accepted the send (promise resolved)
      destroyCaptcha();
      setConfirmation(conf);
      localStorage.setItem(OTP_LOG_KEY, JSON.stringify([...status.log, Date.now()]));
      setCooldown(60);
      toast.success(`OTP sent to ${normalized}`);
    } catch (e) {
      console.error("OTP send failed:", e?.code || e, e);
      if (e?.code === "auth/provider-already-linked") {
        // Phone already linked on Firebase — just sync it to our backend
        try {
          await syncPhoneToBackend();
        } catch (syncErr) {
          setErr(apiError(syncErr));
        }
        return;
      }
      destroyCaptcha();
      if (e?.code === "auth/too-many-requests") {
        setErr("Too many attempts. Please wait 15–60 minutes and try again.");
      } else if (e?.code === "auth/invalid-phone-number") {
        setErr("Enter a valid mobile number, e.g. +91 98765 43210");
      } else if (e?.code === "auth/captcha-check-failed" || /captcha/i.test(e?.message || "")) {
        setCaptchaMode("visible");
        setErr("Security check needed — tick the reCAPTCHA box below, then press Send OTP again.");
      } else if (e?.code === "auth/network-request-failed") {
        setErr("Network error — please retry.");
      } else {
        setErr(fbError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmOtp = async (e) => {
    e.preventDefault();
    if (!confirmation || code.trim().length < 6) return;
    setBusy(true);
    setErr("");
    try {
      await confirmation.confirm(code.trim());
      await syncPhoneToBackend();
    } catch (er) {
      console.error("OTP confirm failed:", er?.code || er);
      setErr(er.code ? fbError(er) : apiError(er));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid="verify-gate-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#059669]" /> Quick verification to unlock this
          </DialogTitle>
        </DialogHeader>

        {!fbUser && (
          <p className="text-sm text-[#475569]" data-testid="verify-relogin-note">
            Your session needs a refresh — log out and sign in again to verify your account.
          </p>
        )}

        {fbUser && step === "email" && (
          <div className="space-y-3" data-testid="verify-email-step">
            <div className="flex items-start gap-3 bg-[#F5F8FF] border border-[#0B1F3A]/10 rounded-md px-3 py-3">
              <MailCheck size={18} className="text-[#1D4ED8] mt-0.5 shrink-0" />
              <p className="text-sm text-[#0B1F3A]">
                First, verify your email. We sent a link to <span className="font-semibold">{user?.email}</span> — click it, then come back here.
              </p>
            </div>
            {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="verify-gate-error">{err}</p>}
            <button onClick={checkEmailVerified} disabled={busy} className="w-full bg-[#1D4ED8] text-white rounded-md py-2.5 text-sm font-medium disabled:opacity-60" data-testid="verify-email-continue-btn">
              {busy ? "Checking…" : "I've clicked the link — continue"}
            </button>
            <button onClick={resendEmail} disabled={emailCooldown > 0} className="w-full border border-[#0B1F3A]/25 text-[#0B1F3A] rounded-md py-2.5 text-sm font-medium disabled:opacity-50" data-testid="verify-email-resend-btn">
              {emailCooldown > 0 ? `Resend email (${emailCooldown}s)` : "Resend verification email"}
            </button>
          </div>
        )}

        {fbUser && step === "phone" && (
          <div className="space-y-3" data-testid="verify-phone-step">
            <div className="flex items-start gap-3 bg-[#F5F8FF] border border-[#0B1F3A]/10 rounded-md px-3 py-3">
              <Smartphone size={18} className="text-[#059669] mt-0.5 shrink-0" />
              <p className="text-sm text-[#0B1F3A]">
                Verify your mobile number with a one-time password — this keeps your reports and saved projects secure. Takes ~30 seconds, once only.
              </p>
            </div>
            {!confirmation ? (
              <>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210"
                  className={inputCls} data-testid="verify-phone-input" />
                {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="verify-gate-error">{err}</p>}
                <button onClick={sendOtp} disabled={busy || cooldown > 0} className="w-full bg-[#059669] text-white rounded-md py-2.5 text-sm font-bold disabled:opacity-60" data-testid="verify-phone-send-btn">
                  {busy ? "Sending OTP…" : cooldown > 0 ? `Resend available in ${cooldown}s` : "Send OTP"}
                </button>
              </>
            ) : (
              <form onSubmit={confirmOtp} className="space-y-3">
                <input inputMode="numeric" autoComplete="one-time-code" name="otp" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="6-digit OTP" className={`${inputCls} tracking-[0.4em] text-center font-mono text-lg`} data-testid="verify-otp-input" autoFocus />
                {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="verify-gate-error">{err}</p>}
                <button type="submit" disabled={busy || code.length < 6} className="w-full bg-[#059669] text-white rounded-md py-2.5 text-sm font-bold disabled:opacity-60" data-testid="verify-otp-confirm-btn">
                  {busy ? "Verifying…" : "Verify & continue"}
                </button>
                <button type="button" onClick={sendOtp} disabled={busy || cooldown > 0} className="w-full text-sm text-[#475569] underline disabled:opacity-50" data-testid="verify-otp-resend-btn">
                  {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
                </button>
              </form>
            )}
            <div key={captchaNonce} id={`otp-recaptcha-${captchaNonce}`} data-testid="otp-recaptcha-container"
              className={captchaMode === "visible" ? "flex justify-center" : ""} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function useVerifyGate() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const pendingRef = useRef(null);

  const verified = !user || user.is_demo || user.role === "admin" || (user.email_verified && user.phone_verified);

  const ensureVerified = useCallback(
    (action) => {
      if (verified) {
        action();
      } else {
        pendingRef.current = action;
        setOpen(true);
      }
    },
    [verified]
  );

  const onVerified = useCallback(() => {
    setOpen(false);
    const action = pendingRef.current;
    pendingRef.current = null;
    if (action) action();
  }, []);

  const gate = <VerifyGate open={open} onOpenChange={setOpen} onVerified={onVerified} />;
  return { ensureVerified, gate };
}
